import type { IChiefEditor, IJournalAuthor } from '@/entities/journal/model/types';
import type { ILawyerCardInfo } from '@/entities/lawyer/model/types/lawyer';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';
import { fetchChiefEditorProfile } from '@/entities/journal/lib/fetchChiefEditorProfile';
import { logError, normalizeError } from '@/shared/lib/errors';

const AUTHORS_PER_PAGE = 6;
const AUTHORS_MIN_RATING = 3;

interface EditorsStaffPayload {
  chiefEditor: Partial<IChiefEditor>;
  authors: ILawyerCardInfo[];
  authorsTotal: number;
}

export const useEditorsStaff = () => {
  const { $journalService } = useNuxtApp();

  const authorsPage = ref(1);

  const fetchAuthors = async (
    page: number,
  ): Promise<{ list: ILawyerCardInfo[]; total: number }> => {
    const response = await $journalService.getAuthors({
      page,
      per_page: AUTHORS_PER_PAGE,
      rating: AUTHORS_MIN_RATING,
      rating_zero: 1,
      without_company: 1,
      sort_rating: 'desc',
    });
    const items: IJournalAuthor[] = Array.isArray(response.data) ? response.data : [];
    const list = items
      .map((author) => author.lawyer)
      .filter((lawyer): lawyer is ILawyerCardInfo => Boolean(lawyer));
    return { list, total: response.meta?.total ?? list.length };
  };

  const pageAsyncData = useAsyncData<EditorsStaffPayload>('editors-staff', async () => {
    try {
      const [chiefEditor, authorsResult] = await Promise.all([
        fetchChiefEditorProfile($journalService, 'editors-staff:topics'),
        fetchAuthors(1),
      ]);
      return {
        chiefEditor,
        authors: authorsResult.list,
        authorsTotal: authorsResult.total,
      };
    } catch (error) {
      logError(normalizeError(error), { action: 'editors-staff-page' });
      throw createError({ statusCode: getErrorStatus(error), fatal: true });
    }
  });

  const { data, status, error } = pageAsyncData;

  watchEffect(() => {
    if (error.value) {
      showError(createError({ statusCode: getErrorStatus(error.value), fatal: true }));
    }
  });

  const chiefEditor: ComputedRef<Partial<IChiefEditor>> = computed(
    () => data.value?.chiefEditor ?? {},
  );
  const authors: ComputedRef<ILawyerCardInfo[]> = computed(
    () => data.value?.authors ?? [],
  );
  const isLoading: ComputedRef<boolean> = computed(() => status.value === 'pending');
  const isShowMore: ComputedRef<boolean> = computed(
    () => authors.value.length < (data.value?.authorsTotal ?? 0),
  );

  const loadMore = async (): Promise<void> => {
    if (!data.value) return;
    authorsPage.value += 1;
    const { list, total } = await fetchAuthors(authorsPage.value);
    data.value = {
      ...data.value,
      authors: [...data.value.authors, ...list],
      authorsTotal: total,
    };
  };

  return {
    chiefEditor,
    authors,
    isLoading,
    isShowMore,
    loadMore,
    pageReady: pageAsyncData,
    pageError: error,
  };
};

export default useEditorsStaff;
